import React, { useEffect, useState } from 'react';
import { Toaster, toast } from 'sonner';
import { MoonStars, Bell, Checks, FloppyDisk, Thermometer } from '@phosphor-icons/react';
import { useI18n } from '../../contexts/I18nContext';
import { useAuth } from '../../contexts/AuthContext';
import { useOrg } from '../../contexts/OrgContext';
import {
  adminGetAlertPolicy, adminUpdateAlertPolicy, adminAckAllOrgAlerts,
  adminGetSensorThresholds, adminUpdateSensorThresholds,
  formatAdminError,
} from '../../lib/admin';
import AdminPage from '../../components/admin/AdminPage';
import ConfirmDialog from '../../components/admin/ConfirmDialog';
import { Field, inputCls } from '../../components/admin/Field';

// Time input always exchanges "HH:mm" strings with the backend. The DB column
// is a `time` value so we normalise to that format here.
const DEFAULT_POLICY = { night_hours_start: '22:00', night_hours_end: '06:00' };

const trimSeconds = (v) => (typeof v === 'string' ? v.slice(0, 5) : v);

export default function AdminAlerts() {
  const { t } = useI18n();
  const { user } = useAuth();
  const { current } = useOrg();
  const orgId = current?.id || null;

  const [form, setForm] = useState(DEFAULT_POLICY);
  const [original, setOriginal] = useState(DEFAULT_POLICY);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [ackBusy, setAckBusy] = useState(false);
  const [confirmAck, setConfirmAck] = useState(false);

  const allowed = user?.role === 'admin';

  useEffect(() => {
    if (!allowed || !orgId) return;
    let cancelled = false;
    setLoading(true);
    adminGetAlertPolicy(orgId)
      .then((p) => {
        if (cancelled) return;
        const normalised = {
          night_hours_start: trimSeconds(p?.night_hours_start) || DEFAULT_POLICY.night_hours_start,
          night_hours_end: trimSeconds(p?.night_hours_end) || DEFAULT_POLICY.night_hours_end,
        };
        setForm(normalised);
        setOriginal(normalised);
      })
      .catch((err) => {
        // 404 → backend endpoint not deployed yet; show defaults so the admin
        // can still set values and save once the endpoint ships.
        if (err?.response?.status !== 404) {
          toast.error(formatAdminError(t('admin_alerts_load_failed'), err));
        }
        setForm(DEFAULT_POLICY);
        setOriginal(DEFAULT_POLICY);
      })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [allowed, orgId, t]);

  const dirty = form.night_hours_start !== original.night_hours_start
    || form.night_hours_end !== original.night_hours_end;

  const onSave = async (e) => {
    e.preventDefault();
    if (!dirty || saving) return;
    setSaving(true);
    try {
      const saved = await adminUpdateAlertPolicy(orgId, {
        night_hours_start: form.night_hours_start,
        night_hours_end: form.night_hours_end,
      });
      const normalised = {
        night_hours_start: trimSeconds(saved?.night_hours_start) || form.night_hours_start,
        night_hours_end: trimSeconds(saved?.night_hours_end) || form.night_hours_end,
      };
      setForm(normalised);
      setOriginal(normalised);
      toast.success(t('admin_alerts_saved'));
    } catch (err) {
      toast.error(formatAdminError(t('admin_alerts_save_failed'), err));
    } finally {
      setSaving(false);
    }
  };

  const onAckAll = async () => {
    setAckBusy(true);
    try {
      const res = await adminAckAllOrgAlerts(orgId);
      const count = typeof res?.acknowledged_count === 'number' ? res.acknowledged_count : null;
      toast.success(count === null
        ? t('admin_alerts_ack_all_done_generic')
        : t('admin_alerts_ack_all_done', { count }));
      setConfirmAck(false);
    } catch (err) {
      toast.error(formatAdminError(t('admin_alerts_ack_all_failed'), err));
    } finally {
      setAckBusy(false);
    }
  };

  if (!allowed) {
    return (
      <AdminPage title={t('admin_alerts_title')}>
        <Toaster position="top-right" richColors />
        <div className="bg-bg-alt rounded-xl p-8 text-center text-ink-soft">{t('admin_admin_only')}</div>
      </AdminPage>
    );
  }

  return (
    <AdminPage
      title={t('admin_alerts_title')}
      description={t('admin_alerts_sub', { org: current?.name || '—' })}
    >
      <Toaster position="top-right" richColors />

      <div className="grid gap-6 md:grid-cols-2" data-testid="admin-alerts-content">
        {/* --- Night hours policy --- */}
        <form
          onSubmit={onSave}
          data-testid="admin-alerts-policy-form"
          className="bg-white border border-stone_line rounded-2xl shadow-soft p-6 space-y-5"
        >
          <header className="flex items-start gap-3">
            <div className="w-10 h-10 rounded-xl bg-sage-100 text-sage-600 grid place-items-center shrink-0">
              <MoonStars size={20} weight="duotone" />
            </div>
            <div>
              <h2 className="font-heading text-2xl text-ink leading-tight">{t('admin_alerts_quiet_title')}</h2>
              <p className="text-sm text-ink-soft mt-1">{t('admin_alerts_quiet_desc')}</p>
            </div>
          </header>

          {loading ? (
            <div className="py-6 text-center text-ink-muted">{t('loading')}</div>
          ) : (
            <>
              <div className="grid grid-cols-2 gap-4">
                <Field label={t('admin_alerts_night_start')} required>
                  <input
                    type="time"
                    required
                    value={form.night_hours_start}
                    onChange={(e) => setForm({ ...form, night_hours_start: e.target.value })}
                    data-testid="admin-alerts-night-start"
                    className={inputCls}
                  />
                </Field>
                <Field label={t('admin_alerts_night_end')} required>
                  <input
                    type="time"
                    required
                    value={form.night_hours_end}
                    onChange={(e) => setForm({ ...form, night_hours_end: e.target.value })}
                    data-testid="admin-alerts-night-end"
                    className={inputCls}
                  />
                </Field>
              </div>
              <p className="text-xs text-ink-muted">{t('admin_alerts_quiet_hint')}</p>

              <div className="flex justify-end pt-2 border-t border-stone_line">
                <button
                  type="submit"
                  disabled={!dirty || saving}
                  data-testid="admin-alerts-save"
                  className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-sage text-white text-sm font-medium hover:bg-sage-700 disabled:opacity-50 transition-colors"
                >
                  <FloppyDisk size={16} weight="bold" />
                  {saving ? '…' : t('save')}
                </button>
              </div>
            </>
          )}
        </form>

        {/* --- Clear all alerts --- */}
        <div
          data-testid="admin-alerts-clear-card"
          className="bg-white border border-stone_line rounded-2xl shadow-soft p-6 space-y-5"
        >
          <header className="flex items-start gap-3">
            <div className="w-10 h-10 rounded-xl bg-terracotta-100 text-terracotta-700 grid place-items-center shrink-0">
              <Bell size={20} weight="duotone" />
            </div>
            <div>
              <h2 className="font-heading text-2xl text-ink leading-tight">{t('admin_alerts_clear_title')}</h2>
              <p className="text-sm text-ink-soft mt-1">{t('admin_alerts_clear_desc')}</p>
            </div>
          </header>

          <p className="text-xs text-ink-muted">{t('admin_alerts_clear_hint')}</p>

          <div className="flex justify-end pt-2 border-t border-stone_line">
            <button
              type="button"
              onClick={() => setConfirmAck(true)}
              disabled={ackBusy}
              data-testid="admin-alerts-ack-all"
              className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-terracotta-700 text-white text-sm font-medium hover:bg-terracotta-800 disabled:opacity-50 transition-colors"
            >
              <Checks size={16} weight="bold" />
              {ackBusy ? t('ack_all_running') : t('admin_alerts_ack_all_button')}
            </button>
          </div>
        </div>
      </div>

      <SensorThresholds orgId={orgId} />

      <ConfirmDialog
        open={confirmAck}
        title={t('admin_alerts_ack_all_title')}
        description={t('admin_alerts_ack_all_confirm', { org: current?.name || '—' })}
        confirmLabel={t('admin_alerts_ack_all_button')}
        danger
        busy={ackBusy}
        onCancel={() => setConfirmAck(false)}
        onConfirm={onAckAll}
      />
    </AdminPage>
  );
}

// Numeric string → number|null (blank/invalid → null).
const numOrNull = (v) => {
  if (v === '' || v == null) return null;
  const n = Number(v);
  return Number.isFinite(n) ? n : null;
};

const THRESH_FIELDS = ['temp_min', 'temp_max', 'humidity_min', 'humidity_max', 'alert_cooldown_min'];
const EMPTY_THRESH = { temp_min: '', temp_max: '', humidity_min: '', humidity_max: '', alert_cooldown_min: '' };

function SensorThresholds({ orgId }) {
  const { t } = useI18n();
  const [form, setForm] = useState(EMPTY_THRESH);
  const [original, setOriginal] = useState(EMPTY_THRESH);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!orgId) return undefined;
    let cancelled = false;
    setLoading(true);
    adminGetSensorThresholds(orgId)
      .then((p) => {
        if (cancelled) return;
        const next = { ...EMPTY_THRESH };
        for (const k of THRESH_FIELDS) next[k] = p?.[k] == null ? '' : String(p[k]);
        setForm(next);
        setOriginal(next);
      })
      .catch((err) => {
        if (err?.response?.status !== 404) toast.error(formatAdminError(t('admin_thresholds_load_failed'), err));
        setForm(EMPTY_THRESH);
        setOriginal(EMPTY_THRESH);
      })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [orgId, t]);

  const dirty = THRESH_FIELDS.some((k) => form[k] !== original[k]);

  const tempInvalid = form.temp_min !== '' && form.temp_max !== ''
    && Number(form.temp_min) >= Number(form.temp_max);
  const humInvalid = form.humidity_min !== '' && form.humidity_max !== ''
    && Number(form.humidity_min) >= Number(form.humidity_max);
  // Cooldown: optional, but if set it must be a positive integer (>= 1).
  const cooldownInvalid = form.alert_cooldown_min !== ''
    && (!Number.isInteger(Number(form.alert_cooldown_min)) || Number(form.alert_cooldown_min) < 1);
  const invalid = tempInvalid || humInvalid || cooldownInvalid;

  const onSave = async (e) => {
    e.preventDefault();
    if (!dirty || saving || invalid) return;
    setSaving(true);
    try {
      const body = {};
      for (const k of THRESH_FIELDS) body[k] = numOrNull(form[k]); // null clears a boundary
      const saved = await adminUpdateSensorThresholds(orgId, body);
      const next = { ...EMPTY_THRESH };
      for (const k of THRESH_FIELDS) {
        const v = saved && k in saved ? saved[k] : body[k];
        next[k] = v == null ? '' : String(v);
      }
      setForm(next);
      setOriginal(next);
      toast.success(t('admin_thresholds_saved'));
    } catch (err) {
      toast.error(formatAdminError(t('admin_thresholds_save_failed'), err));
    } finally {
      setSaving(false);
    }
  };

  const numInput = (key, unit, testid) => (
    <div>
      <input
        type="number" step="0.1" inputMode="decimal"
        value={form[key]}
        onChange={(e) => setForm({ ...form, [key]: e.target.value })}
        data-testid={testid}
        placeholder="—"
        className={inputCls}
      />
      <span className="text-[11px] text-ink-muted mt-1 block">{unit}</span>
    </div>
  );

  return (
    <form
      onSubmit={onSave}
      data-testid="admin-thresholds-form"
      className="bg-white border border-stone_line rounded-2xl shadow-soft p-6 space-y-5 mt-6"
    >
      <header className="flex items-start gap-3">
        <div className="w-10 h-10 rounded-xl bg-sky-100 text-sky-700 grid place-items-center shrink-0">
          <Thermometer size={20} weight="duotone" />
        </div>
        <div>
          <h2 className="font-heading text-2xl text-ink leading-tight">{t('admin_thresholds_title')}</h2>
          <p className="text-sm text-ink-soft mt-1">{t('admin_thresholds_desc')}</p>
        </div>
      </header>

      {loading ? (
        <div className="py-6 text-center text-ink-muted">{t('loading')}</div>
      ) : (
        <>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
            <Field label={t('admin_thresholds_temp_min')}>{numInput('temp_min', '°C', 'admin-threshold-temp-min')}</Field>
            <Field label={t('admin_thresholds_temp_max')}>{numInput('temp_max', '°C', 'admin-threshold-temp-max')}</Field>
            <Field label={t('admin_thresholds_hum_min')}>{numInput('humidity_min', '%', 'admin-threshold-hum-min')}</Field>
            <Field label={t('admin_thresholds_hum_max')}>{numInput('humidity_max', '%', 'admin-threshold-hum-max')}</Field>
          </div>

          {tempInvalid && (
            <p className="text-xs text-terracotta-700" data-testid="admin-threshold-temp-error">{t('admin_thresholds_temp_invalid')}</p>
          )}
          {humInvalid && (
            <p className="text-xs text-terracotta-700" data-testid="admin-threshold-hum-error">{t('admin_thresholds_hum_invalid')}</p>
          )}

          <Field label={t('admin_thresholds_cooldown')}>
            <div className="max-w-[220px]">
              <input
                type="number" step="1" min="1" inputMode="numeric"
                value={form.alert_cooldown_min}
                onChange={(e) => setForm({ ...form, alert_cooldown_min: e.target.value })}
                data-testid="admin-threshold-cooldown"
                placeholder={t('admin_thresholds_cooldown_placeholder')}
                className={inputCls}
              />
              <span className="text-[11px] text-ink-muted mt-1 block">{t('admin_thresholds_cooldown_help')}</span>
            </div>
          </Field>
          {cooldownInvalid && (
            <p className="text-xs text-terracotta-700" data-testid="admin-threshold-cooldown-error">{t('admin_thresholds_cooldown_invalid')}</p>
          )}

          <p className="text-xs text-ink-muted">{t('admin_thresholds_hint')}</p>

          <div className="flex justify-end pt-2 border-t border-stone_line">
            <button
              type="submit"
              disabled={!dirty || saving || invalid}
              data-testid="admin-thresholds-save"
              className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-sage text-white text-sm font-medium hover:bg-sage-700 disabled:opacity-50 transition-colors"
            >
              <FloppyDisk size={16} weight="bold" />
              {saving ? '…' : t('save')}
            </button>
          </div>
        </>
      )}
    </form>
  );
}
